import { useState } from "react";
import { AssetResultRow } from "~/modules/asset_screener/components/AssetResultRow";
import type { ScreenerAsset } from "~/modules/asset_screener/types";
import { InfiniteScrollTracker } from "~/components/InfiniteScrollTracker";
import { LoadingSpinner } from "~/components/LoadingSpinner";
import { cn } from "~/lib/utils";

// // // //

/**
 * AssetScreenerList
 * Renders screener assets as expandable AssetResultRows, tracking which rows
 * are expanded. Loads the next page via an InfiniteScrollTracker and, while
 * fetching, dims the rows and shows a spinner.
 */
export function AssetScreenerList(props: {
    assets: ScreenerAsset[];
    fetching: boolean;
    hasNextPage: boolean;
    isLoggedIn: boolean;
    loadNextPage: () => void;
}) {
    const { assets, fetching, hasNextPage, loadNextPage, isLoggedIn } = props;

    const [expandedAssetIds, setExpandedAssetIds] = useState<Set<number>>(
        new Set(),
    );

    return (
        <>
            <div
                className={cn("transition-opacity", {
                    "opacity-50": fetching,
                })}
            >
                {assets.map((asset, index) => (
                    <AssetResultRow
                        key={asset.id}
                        asset={asset}
                        isLoggedIn={isLoggedIn}
                        isExpanded={expandedAssetIds.has(asset.id)}
                        hasTopBorder={index === 0}
                        isFirstRow={index === 0}
                        isLastRow={index === assets.length - 1}
                        onToggle={() => {
                            setExpandedAssetIds((prev) => {
                                const newSet = new Set(prev);
                                if (newSet.has(asset.id)) {
                                    newSet.delete(asset.id);
                                } else {
                                    newSet.add(asset.id);
                                }
                                return newSet;
                            });
                        }}
                    />
                ))}
            </div>

            {hasNextPage && (
                <InfiniteScrollTracker
                    onIntersect={loadNextPage}
                    threshold={0}
                    rootMargin="400px"
                    invisible
                />
            )}

            {fetching && (
                <div className="flex items-center justify-center gap-3 py-6 text-sm text-muted-foreground">
                    <LoadingSpinner size={5} />
                    {"Loading assets..."}
                </div>
            )}
        </>
    );
}
